import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const PredictiveAccuracyTab = ({ dateRange }) => {
  const actualVsPredictedData = [
    { month: 'Jan', actual: 45000, predicted: 48000, variance: -6.25 },
    { month: 'Feb', actual: 52000, predicted: 54000, variance: -3.70 },
    { month: 'Mar', actual: 48000, predicted: 51000, variance: -5.88 },
    { month: 'Apr', actual: 61000, predicted: 63000, variance: -3.17 },
    { month: 'May', actual: 55000, predicted: 58000, variance: -5.17 },
    { month: 'Jun', actual: 67000, predicted: 70000, variance: -4.29 }
  ];

  const modelConfidenceData = [
    { month: 'Jan', confidence: 85, accuracy: 82 },
    { month: 'Feb', confidence: 87, accuracy: 85 },
    { month: 'Mar', confidence: 88, accuracy: 86 },
    { month: 'Apr', confidence: 90, accuracy: 89 },
    { month: 'May', confidence: 91, accuracy: 90 },
    { month: 'Jun', confidence: 92, accuracy: 92 }
  ];

  const recommendationEffectiveness = [
    { category: 'Revenue Growth', implemented: 12, successful: 10, effectiveness: 83 },
    { category: 'Cost Reduction', implemented: 8, successful: 7, effectiveness: 88 },
    { category: 'Market Expansion', implemented: 5, successful: 3, effectiveness: 60 },
    { category: 'Product Launch', implemented: 4, successful: 4, effectiveness: 100 }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-md p-3 elevation-lg">
          <p className="text-sm font-medium text-foreground font-body mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground font-caption">
                {entry?.name}:
              </span>
              <span className="font-medium text-foreground font-body">
                {entry?.name?.includes('Revenue') || entry?.name?.includes('actual') || entry?.name?.includes('predicted')
                  ? `₹${entry?.value?.toLocaleString()}`
                  : entry?.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const averageAccuracy = modelConfidenceData?.reduce((sum, item) => sum + item?.accuracy, 0) / modelConfidenceData?.length;
  const averageVariance = actualVsPredictedData?.reduce((sum, item) => sum + Math.abs(item?.variance), 0) / actualVsPredictedData?.length;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Accuracy Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-card border border-border rounded-lg p-3 md:p-4 elevation-sm">
          <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
              <Icon name="CheckCircle" size={16} className="md:w-[18px] md:h-[18px]" color="var(--color-success)" />
            </div>
            <h4 className="text-xs md:text-sm font-medium text-muted-foreground font-caption break-words">
              Average Accuracy
            </h4>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-success font-heading">
            {averageAccuracy?.toFixed(1)}%
          </p>
          <p className="text-xs text-muted-foreground font-caption mt-2 break-words">
            Prediction reliability score
          </p>
        </div>
        <div className="bg-card border border-border rounded-lg p-3 md:p-4 elevation-sm">
          <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
              <Icon name="AlertCircle" size={16} className="md:w-[18px] md:h-[18px]" color="var(--color-warning)" />
            </div>
            <h4 className="text-xs md:text-sm font-medium text-muted-foreground font-caption break-words">
              Average Variance
            </h4>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-warning font-heading">
            {averageVariance?.toFixed(2)}%
          </p>
          <p className="text-xs text-muted-foreground font-caption mt-2 break-words">
            Deviation from predictions
          </p>
        </div>
        <div className="bg-card border border-border rounded-lg p-3 md:p-4 elevation-sm">
          <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Icon name="TrendingUp" size={16} className="md:w-[18px] md:h-[18px]" color="var(--color-accent)" />
            </div>
            <h4 className="text-xs md:text-sm font-medium text-muted-foreground font-caption break-words">
              Model Confidence
            </h4>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-accent font-heading">92%</p>
          <p className="text-xs text-muted-foreground font-caption mt-2 break-words">
            Current prediction confidence
          </p>
        </div>
      </div>

      {/* Actual vs Predicted Revenue */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
        <div className="flex items-start gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="BarChart3" size={18} className="md:w-5 md:h-5" color="var(--color-primary)" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground font-heading break-words">
              Actual vs Predicted Revenue
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground font-caption break-words">
              Compare forecasted revenue with actual performance
            </p>
          </div>
        </div>
        <div className="w-full h-64 md:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={actualVsPredictedData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="month"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '10px', fontFamily: 'Source Sans 3' }}
                className="md:text-xs"
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '10px', fontFamily: 'Source Sans 3' }}
                className="md:text-xs"
                tickFormatter={(value) => `₹${(value / 1000)?.toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '10px', fontFamily: 'Source Sans 3' }} className="md:text-xs" />
              <Line
                type="monotone"
                dataKey="actual"
                stroke="var(--color-primary)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-primary)', r: 3 }}
                name="Actual Revenue"
              />
              <Line
                type="monotone"
                dataKey="predicted"
                stroke="var(--color-secondary)"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: 'var(--color-secondary)', r: 2 }}
                name="Predicted Revenue"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Model Confidence Tracking */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
        <div className="flex items-start gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Target" size={18} className="md:w-5 md:h-5" color="var(--color-accent)" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground font-heading break-words">
              Model Confidence & Accuracy Tracking
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground font-caption break-words">
              Monitor prediction reliability over time
            </p>
          </div>
        </div>
        <div className="w-full h-64 md:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={modelConfidenceData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="month"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '10px', fontFamily: 'Source Sans 3' }}
                className="md:text-xs"
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '10px', fontFamily: 'Source Sans 3' }}
                className="md:text-xs"
                domain={[0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '10px', fontFamily: 'Source Sans 3' }} className="md:text-xs" />
              <Line
                type="monotone"
                dataKey="confidence"
                stroke="var(--color-accent)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-accent)', r: 3 }}
                name="Confidence Score"
              />
              <Line
                type="monotone"
                dataKey="accuracy"
                stroke="var(--color-success)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-success)', r: 2 }}
                name="Accuracy Score"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recommendation Effectiveness */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
        <div className="flex items-start gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Lightbulb" size={18} className="md:w-5 md:h-5" color="var(--color-secondary)" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground font-heading break-words">
              AI Recommendation Effectiveness
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground font-caption break-words">
              Success rate of implemented AI suggestions
            </p>
          </div>
        </div>
        <div className="space-y-3 md:space-y-4">
          {recommendationEffectiveness?.map((rec, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs md:text-sm font-medium text-foreground font-body break-words">
                  {rec?.category}
                </span>
                <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                  <span className="text-xs text-muted-foreground font-caption">
                    {rec?.successful}/{rec?.implemented} successful
                  </span>
                  <span className={`text-xs md:text-sm font-semibold font-body ${
                    rec?.effectiveness >= 80 ? 'text-success' : rec?.effectiveness >= 60 ? 'text-warning' : 'text-error'
                  }`}>
                    {rec?.effectiveness}%
                  </span>
                </div>
              </div>
              <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 rounded-full transition-smooth ${
                    rec?.effectiveness >= 80 ? 'bg-success' : rec?.effectiveness >= 60 ? 'bg-warning' : 'bg-error'
                  }`}
                  style={{ width: `${rec?.effectiveness}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PredictiveAccuracyTab;